import { cellToBoundary } from 'h3-js';
import { useEffect, useState } from 'react';
import { GameService } from '../../services';
import { useCancellablePromise } from '../cancellable-promise';
import { useSource } from './source';
export const gameTilesSourceId = 'game-h3-cells';
export const gameTilesLayerId = 'game-h3-cells';
export const gameTilesLevelsSourceId = 'game-h3-cells-levels';
export const gameTilesLevelsLayerId = 'game-h3-cells-levels';
export function useGameTiles({
  game,
  map,
  mapInitialized,
  teams: providedTeams,
  onError
}) {
  const [sourcesInitialized, setSourcesInitialized] = useState(false);
  const [fetchedTeams, setFetchedTeams] = useState();
  const [tiles, setTiles] = useState();
  const teams = providedTeams || fetchedTeams;
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  const {
    addGeoJSONSource,
    updateGeoJSONSource,
    clearGeoJSONSource
  } = useSource(map, gameTilesSourceId);
  const {
    addGeoJSONSource: addLevelsSource,
    updateGeoJSONSource: updateLevelsSource,
    clearGeoJSONSource: clearLevelsSource
  } = useSource(map, gameTilesLevelsSourceId);
  useEffect(() => {
    async function getData(gameId) {
      try {
        const _tiles = await cancellablePromise(providedTeams ? GameService.getTiles(gameId) : Promise.all([GameService.getTeams(gameId, {
          query: '{id title color icon}'
        }), GameService.getTiles(gameId)]).then(([_teams, tiles]) => {
          setFetchedTeams(_teams);
          return tiles;
        }));
        setTiles(_tiles);
      } catch (err) {
        if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
          console.error(err);
          if (err instanceof Error) onError?.(err);
        }
      }
    }
    if (game) getData(game.id);
    return () => {
      cancelPromises();
      setFetchedTeams(undefined);
      setTiles(undefined);
    };
  }, [game, Boolean(providedTeams)]);
  useEffect(() => {
    if (map && mapInitialized && !map.getLayer(gameTilesLayerId)) {
      addGeoJSONSource();
      addLevelsSource();

      // 'place_other' is the game's own dedicated style's label layer — falls back to appending
      // on top (no anchor) if it's ever missing.
      const beforeId = map.getLayer('place_other') ? 'place_other' : undefined;
      map.addLayer({
        id: gameTilesLayerId,
        type: 'fill',
        source: gameTilesSourceId,
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': ['get', 'opacity'],
          'fill-outline-color': game?.mode === 'BLOOM' ? 'rgba(0, 0, 0, 0)' : ['get', 'color']
        },
        filter: ['==', 'has_team', true]
      }, beforeId);
      if (game?.mode !== 'BLOOM') {
        map.addLayer({
          id: `${gameTilesLayerId}-borders`,
          type: 'line',
          source: gameTilesSourceId,
          minzoom: 11,
          paint: {
            'line-color': '#fff',
            'line-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 18, 2]
          }
        }, beforeId);
      }
      map.addLayer({
        id: gameTilesLevelsLayerId,
        type: 'circle',
        source: gameTilesLevelsSourceId,
        minzoom: 11,
        paint: {
          'circle-color': ['get', 'color'],
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 5, 18, 30],
          'circle-stroke-color': '#fff',
          'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 18, 2]
        }
      });
      map.addLayer({
        id: `${gameTilesLevelsLayerId}-text`,
        type: 'symbol',
        source: gameTilesLevelsSourceId,
        minzoom: 11,
        layout: {
          'symbol-placement': 'point',
          'text-field': '{level}',
          'text-allow-overlap': true,
          'text-font': ['Nunito Sans Bold'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 18, 20]
        },
        paint: {
          'text-color': '#fff'
        }
      });
      setSourcesInitialized(true);
    }
  }, [map, mapInitialized]);
  useEffect(() => {
    if (!sourcesInitialized || !teams || !tiles) return;
    const teamsMap = teams.reduce((res, team) => {
      res[team.id] = team;
      return res;
    }, {});
    const cellsMap = {};
    for (const {
      h3Index
    } of tiles) {
      const coordinates = cellToBoundary(h3Index, true);
      cellsMap[h3Index] = {
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        },
        levelPosition: coordinates[4]
      };
    }
    updateGeoJSONSource({
      type: 'FeatureCollection',
      features: tiles.map(({
        h3Index,
        teamId
      }) => {
        const color = teamId ? teamsMap[teamId]?.color || '#eac28e' : undefined;
        const {
          geometry
        } = cellsMap[h3Index];
        return {
          id: h3Index,
          type: 'Feature',
          geometry,
          properties: {
            h3Index,
            has_team: Boolean(teamId),
            color,
            opacity: 0.5
          }
        };
      })
    });
    updateLevelsSource({
      type: 'FeatureCollection',
      features: tiles.reduce((res, {
        h3Index,
        teamId,
        metadata
      }) => {
        if (metadata?.rubi_level) {
          const color = teamId ? teamsMap[teamId]?.color || '#eac28e' : undefined;
          const {
            levelPosition: [lng, lat]
          } = cellsMap[h3Index];
          res.push({
            id: h3Index,
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [lng, lat]
            },
            properties: {
              h3Index,
              level: `${metadata.rubi_level}`,
              color
            }
          });
        }
        return res;
      }, [])
    });
    return () => {
      clearGeoJSONSource();
      clearLevelsSource();
    };
  }, [sourcesInitialized, teams, tiles]);
  return {
    teams,
    tiles
  };
}